export default function SolutionSection() {
  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Constitutional AI
          </h2>
          <p className="text-xl text-slate-700 leading-relaxed">
            The solution isn't to abandon AI—it's to build systems with explicit constitutional constraints rather than implicit historical biases.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Bad Approach */}
          <div className="bg-white border-4 border-red-800 p-10 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-red-800 text-white mr-4">
                <i className="ri-close-line text-2xl"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Inverse Reinforcement Learning
              </h3>
            </div>
            <div className="space-y-4 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-red-800">The Problem:</strong> Training AI to infer human values from historical behavior assumes our past actions reflect our true values.
              </p>
              <p className="leading-relaxed">
                This approach systematically encodes discrimination, inconsistency, and bias because it treats revealed preferences as normative standards.
              </p>
              <ul className="space-y-2 ml-6">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                  <span>Amplifies historical discrimination</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                  <span>Obscures accountability</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                  <span>Creates legal liability</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Good Approach */}
          <div className="bg-white border-4 border-slate-900 p-10 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white mr-4">
                <i className="ri-check-line text-2xl"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Explicit Constraints
              </h3>
            </div>
            <div className="space-y-4 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">The Solution:</strong> Define constitutional principles explicitly and build AI systems that operate within those boundaries from the ground up.
              </p>
              <p className="leading-relaxed">
                This approach ensures AI systems align with our stated values, not our historical failures, creating transparent and accountable decision-making.
              </p>
              <ul className="space-y-2 ml-6">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-slate-900 mt-1 mr-2"></i>
                  <span>Transparent value alignment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-slate-900 mt-1 mr-2"></i>
                  <span>Clear accountability mechanisms</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-slate-900 mt-1 mr-2"></i>
                  <span>Proactive bias prevention</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mt-16 bg-slate-900 text-white p-10">
          <p className="text-lg leading-relaxed text-center">
            <strong className="text-xl">Our Approach:</strong> We advocate for updating the NIST AI Risk Management Framework to require explicit constitutional constraints in high-stakes AI systems, particularly those affecting civil rights, employment, housing, and criminal justice.
          </p>
        </div>
      </div>
    </section>
  );
}
